import React, { memo, useCallback, useState, useEffect } from 'react';
import { Handle, Position, useReactFlow, useEdges, useNodes } from 'reactflow';
import CustomResizer from './CustomResizer';
import { Input, Button, message, Spin } from 'antd';
import { FileTextOutlined, PlayCircleFilled, LoadingOutlined, FontSizeOutlined, SaveOutlined } from '@ant-design/icons';

import NodeLabel from './NodeLabel';
import SavePromptModal from '../components/SavePromptModal';
import api from '../../../api/client';
import { TYPE_COLORS, getHandleStyle, HANDLE_ICON_STYLE } from './nodeStyles';

import { useNodeTitle } from './useNodeTitle';

const { TextArea } = Input;

const TextNode = ({ id, data, selected }) => {
    const { setNodes } = useReactFlow();
    const allEdges = useEdges();
    const allNodes = useNodes();
    const { onSave } = data;
    const onTitleChange = useNodeTitle(id, data);

    const [currText, setCurrText] = useState(data.text || '');
    const [systemPrompt, setSystemPrompt] = useState(data.systemPrompt || '');
    const [generating, setGenerating] = useState(false);
    const [textFocused, setTextFocused] = useState(false);
    const [promptFocused, setPromptFocused] = useState(false);
    const [savePromptModalVisible, setSavePromptModalVisible] = useState(false);
    const [saveSystemPromptModalVisible, setSaveSystemPromptModalVisible] = useState(false);
    const [hovered, setHovered] = useState(false);

    // Handles are visible when node is selected or hovered
    const handlesVisible = selected || hovered;

    // Sync local state when external data changes, but ONLY if not currently editing
    useEffect(() => {
        if (!textFocused) {
            setCurrText(data.text || '');
        }
    }, [data.text, textFocused]);

    useEffect(() => {
        if (!promptFocused && data.systemPrompt !== undefined) {
            setSystemPrompt(data.systemPrompt);
        }
    }, [data.systemPrompt, promptFocused]);

    const updateStoreData = useCallback((key, value) => {
        setNodes((nodes) =>
            nodes.map((node) => {
                if (node.id === id) {
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            [key]: value,
                        },
                    };
                }
                return node;
            })
        );
    }, [id, setNodes]);

    const persistNodeData = useCallback((key, value) => {
        if (onSave) {
            onSave(id, { [key]: value });
        }
    }, [id, onSave]);

    const onChange = useCallback((evt) => {
        const val = evt.target.value;
        setCurrText(val);
        updateStoreData('text', val);
    }, [updateStoreData]);

    const onSystemPromptChange = useCallback((evt) => {
        setSystemPrompt(evt.target.value);
    }, []);

    const onBlur = useCallback(() => {
        setTextFocused(false);
        persistNodeData('text', currText);
    }, [currText, persistNodeData]);

    const onFocus = useCallback(() => {
        setTextFocused(true);
    }, []);

    const onSystemPromptBlur = useCallback(() => {
        setPromptFocused(false);
        persistNodeData('systemPrompt', systemPrompt);
    }, [systemPrompt, persistNodeData]);

    const onSystemPromptFocus = useCallback(() => {
        setPromptFocused(true);
    }, []);

    const handleGenerate = async () => {
        setGenerating(true);
        try {
            // Find input from left handle
            const inputEdge = allEdges.find(
                (edge) => edge.target === id && (edge.targetHandle === 'left-input' || edge.targetHandle === null)
            );

            let userPrompt = '';

            if (inputEdge) {
                const sourceNode = allNodes.find((node) => node.id === inputEdge.source);
                if (sourceNode && sourceNode.data && sourceNode.data.text) {
                    userPrompt = sourceNode.data.text;
                }
            }

            const response = await api.post('/groq/generate', {
                system: systemPrompt,
                user: userPrompt
            });

            if (response.data && response.data.text) {
                const generatedText = response.data.text;
                setCurrText(generatedText);
                updateStoreData('text', generatedText);
                persistNodeData('text', generatedText);
                message.success('Text generated!');
            }
        } catch (error) {
            console.error('Generation failed:', error);
            message.error('Failed to generate text.');
        } finally {
            setGenerating(false);
        }
    };

    const handleSavePrompt = async (values) => {
        try {
            await api.post('/prompts', {
                ...values,
                content: currText
            });
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const handleSaveSystemPrompt = async (values) => {
        try {
            await api.post('/system-prompts', {
                ...values,
                content: systemPrompt
            });
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const isGenerator = data.nodeTitle === 'Text Generator';

    return (
        <div
            style={{
                background: 'transparent',
                padding: 20,
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onDoubleClick={(e) => e.stopPropagation()}
        >
            <CustomResizer
                isVisible={selected}
                minWidth={150}
                minHeight={100}
                onResizeEnd={(event, params) => {
                    const { width, height, x, y } = params;
                    if (data.onResize) {
                        data.onResize(id, { width, height, x, y });
                    }
                }}
            />
            <NodeLabel
                title={data.nodeTitle || 'Text'}
                iconType={data.iconType || 'file-text'}
                color={data.nodeColor || '#fa8c16'}
                onTitleChange={onTitleChange}
            />

            {/* Input Handle - Invisible Edge Zone */}
            {isGenerator && (
                <Handle
                    type="target"
                    position={Position.Left}
                    id="left-input"
                    style={getHandleStyle(TYPE_COLORS.text, 'left', handlesVisible)}
                />
            )}

            <div style={{
                background: '#161b22',
                borderRadius: 8,
                border: '1px solid #30363d',
                boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                display: 'flex',
                gap: 10,
                padding: 10,
                flex: 1,
                flexDirection: 'row',
                height: '100%',
                position: 'relative' // For handles
            }}>
                <div className="nodrag" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, height: '100%' }}>

                    {/* System Prompt Input */}
                    {isGenerator && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: 10, fontWeight: 'bold', color: '#8b949e', textTransform: 'uppercase' }}>System Prompt</span>
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<SaveOutlined />}
                                    onClick={() => setSaveSystemPromptModalVisible(true)}
                                    style={{ fontSize: 10, height: 20, padding: '0 4px', color: '#8b949e' }}
                                >
                                    Save
                                </Button>
                            </div>
                            <TextArea
                                value={systemPrompt}
                                onChange={onSystemPromptChange}
                                onBlur={onSystemPromptBlur}
                                onFocus={onSystemPromptFocus}
                                placeholder="You are a helpful assistant..."
                                autoSize={{ minRows: 2, maxRows: 6 }}
                                style={{
                                    background: '#0d1117',
                                    border: '1px solid #30363d',
                                    color: '#c9d1d9',
                                    fontSize: 12,
                                    resize: 'none'
                                }}
                            />
                        </div>
                    )}

                    {/* Main Output / Text Area */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, minHeight: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            {isGenerator && <span style={{ fontSize: 10, fontWeight: 'bold', color: '#8b949e', textTransform: 'uppercase' }}>Generated Output</span>}
                            {!isGenerator && (
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<SaveOutlined />}
                                    onClick={() => setSavePromptModalVisible(true)}
                                    style={{ fontSize: 10, height: 20, padding: '0 4px', color: '#8b949e', marginLeft: 'auto' }}
                                >
                                    Save Prompt
                                </Button>
                            )}
                        </div>
                        <TextArea
                            value={currText}
                            onChange={onChange}
                            onBlur={onBlur}
                            onFocus={onFocus}
                            bordered={false}
                            placeholder={isGenerator ? "Output will appear here..." : "Type something..."}
                            style={{
                                padding: isGenerator ? 8 : 0,
                                resize: 'none',
                                fontSize: 14,
                                fontFamily: 'Inter, sans-serif',
                                height: '100%',
                                width: '100%',
                                color: '#c9d1d9',
                                background: isGenerator ? '#0d1117' : 'transparent',
                                border: isGenerator ? '1px solid #30363d' : 'none',
                                borderRadius: isGenerator ? 4 : 0
                            }}
                        />
                    </div>

                    {/* Generate Button */}
                    {isGenerator && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 8, borderTop: '1px solid #21262d' }}>
                            <Button
                                type="primary"
                                icon={generating ? <LoadingOutlined /> : <PlayCircleFilled />}
                                onClick={handleGenerate}
                                loading={generating}
                                style={{ background: '#52c41a', borderColor: '#52c41a' }}
                            >
                                Generate
                            </Button>
                        </div>
                    )}

                </div>
            </div>

            {/* Output Handle - Invisible Edge Zone */}
            <Handle
                type="source"
                position={Position.Right}
                id="right-output"
                style={getHandleStyle(TYPE_COLORS.text, 'right', handlesVisible)}
            />

            {/* Modals */}
            <SavePromptModal
                visible={savePromptModalVisible}
                onCancel={() => setSavePromptModalVisible(false)}
                onSave={handleSavePrompt}
                title="Save Prompt"
            />

            <SavePromptModal
                visible={saveSystemPromptModalVisible}
                onCancel={() => setSaveSystemPromptModalVisible(false)}
                onSave={handleSaveSystemPrompt}
                title="Save System Prompt"
            />
        </div>
    );
};

export default memo(TextNode);
